import { useCategoryGroups } from "@/hooks/useCategoryGroups";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CategoryGroupForm } from "@/components/admin/CategoryGroupForm";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Category, CategoryGroup } from "@/types/category";
import { Badge } from "@/components/ui/badge";

interface CategoryGroupsTabProps {
  categoriesByGroup: Record<string, Category[]>;
}

export const CategoryGroupsTab = ({ categoriesByGroup }: CategoryGroupsTabProps) => {
  const { categoryGroups, isLoading, mutation, deleteMutation } = useCategoryGroups();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CategoryGroup | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
  }>({
    name: '',
    description: ''
  });

  const openAddForm = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      description: ''
    });
    setIsFormOpen(true);
  };

  const openEditForm = (group: CategoryGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || ''
    });
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingGroup(null);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setConfirmDelete(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      id: editingGroup?.id,
      name: formData.name,
      description: formData.description
    });
    handleFormClose();
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Category Groups</h2>
        <Button onClick={openAddForm}>
          <Plus className="mr-2 h-4 w-4" /> Add Group
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading category groups...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categoryGroups?.map((group) => (
            <Card key={group.id}>
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center space-x-2">
                  <CardTitle className="text-lg">{group.name}</CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {group.quoteCount} quotes
                  </Badge>
                </div>
                <div className="flex space-x-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => openEditForm(group)}
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    onClick={() => setConfirmDelete(group.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-3 px-4">
                <div className="flex flex-wrap gap-1">
                  {categoriesByGroup[group.id]?.map((category) => (
                    <Badge key={category.id} variant="outline" className="text-xs">
                      {category.name}
                    </Badge>
                  ))}
                  {(!categoriesByGroup[group.id] || categoriesByGroup[group.id]?.length === 0) && (
                    <span className="text-sm text-muted-foreground">No categories</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {categoryGroups?.length === 0 && (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              No category groups found. Create your first group to get started.
            </div>
          )}
        </div>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingGroup ? "Edit Group" : "Add Group"}</DialogTitle>
            <DialogDescription>
              {editingGroup 
                ? "Make changes to your category group here." 
                : "Create a new category group to organize your quotes."}
            </DialogDescription>
          </DialogHeader>
          <CategoryGroupForm
            name={formData.name}
            description={formData.description}
            isLoading={mutation.isPending}
            onSubmit={handleSubmit}
            onNameChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
            onDescriptionChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this category group. Categories in this group will remain but will become ungrouped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
